import { useTranslation } from 'react-i18next';
import {
    BarChart3,
    FileText,
    Search,
    UserCheck,
    Users,
} from 'lucide-react';
import { ScoreCard, SignalCard } from '../../dashboard';
import type { FacilityMetricsResult } from '../../../services/api/facilityMetricsService';
import {
    formatFacilityDisplayDate,
    getDaysRemaining,
    type Facility,
} from '../utils/utils';
import {
    toSignalCardProps,
    type SignalDisplayConfig,
} from '../signalCardProps';

interface TrialOverviewProps {
    facility: Facility;
    metrics: FacilityMetricsResult;
}

export default function TrialOverview({
    facility,
    metrics,
}: TrialOverviewProps) {
    const { t } = useTranslation();

    const SCORE_LABELS: Record<string, string> = {
        trial: t('facilities.trial.scoreLabels.trial'),
        subscription: t('facilities.trial.scoreLabels.subscription'),
    };

    const TRIAL_SIGNAL_CONFIGS: Record<string, SignalDisplayConfig> = {
        usersLoggedInPercent: {
            icon: <UserCheck className="h-4 w-4" />,
            tooltip: t('facilities.trial.tooltips.usersLoggedIn'),
        },
        usersSearchedPercent: {
            icon: <Search className="h-4 w-4" />,
            tooltip: t('facilities.trial.tooltips.usersSearched'),
        },
        averageSearchesPerUser: {
            icon: <BarChart3 className="h-4 w-4" />,
            tooltip: t('facilities.trial.tooltips.averageSearches'),
            visual: 'sparkline',
            trendFormatter: (v) => t('facilities.trial.searchCount', { count: v }),
            subtitle: t('facilities.trial.perUser'),
        },
        multipleActiveUsers: {
            icon: <Users className="h-4 w-4" />,
            tooltip: t('facilities.trial.tooltips.multipleActiveUsers'),
        },
        // CPT — US-only, not translated
        cptReportsCreated: {
            icon: <FileText className="h-4 w-4" />,
            tooltip: 'Number of CPT reports created.',
            visual: 'bars',
            subtitle: 'reports',
        },
    };

    const scoreLabel = SCORE_LABELS[metrics.stage] || t('facilities.trial.scoreLabels.default');
    const visibleSignals = Array.isArray(metrics.signals)
        ? metrics.signals
        : [];

    const trialRangeLabel = facility.start_date
        ? `${t('facilities.trial.trialLabel')} ${formatFacilityDisplayDate(facility.start_date)}${
              facility.end_date
                  ? ` — ${formatFacilityDisplayDate(facility.end_date)}`
                  : ''
          }`
        : t('facilities.trial.trialFallback');

    const trialDaysRemaining = getDaysRemaining(
        facility.start_date,
        'trial',
        facility.end_date
    );
    const trialCountdown =
        trialDaysRemaining == null
            ? null
            : trialDaysRemaining > 0
              ? t('facilities.trial.daysRemaining', { days: trialDaysRemaining })
              : trialDaysRemaining === 0
                ? t('facilities.trial.endsToday')
                : t('facilities.trial.expiredAgo', { days: Math.abs(trialDaysRemaining) });
    const trialCountdownClass =
        trialDaysRemaining != null && trialDaysRemaining < 0
            ? 'border-rose-500/20 bg-rose-500/10 text-rose-400'
            : 'border-amber-500/20 bg-amber-500/10 text-amber-500';
    // Trial signals are displayed in a 5-column grid on the US trial overview page
    const isFiveLayout = visibleSignals.length === 5;

    return (
        <div className="mb-12">
            <div className="mb-3">
                <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-xl font-semibold text-foreground">
                        {trialRangeLabel}
                    </h3>
                    {trialCountdown && (
                        <span
                            className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${trialCountdownClass}`}
                        >
                            {trialCountdown}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
                <ScoreCard
                    title={scoreLabel}
                    score={metrics.score}
                    layout="vertical"
                    className="lg:w-[280px] lg:shrink-0"
                />
                <div
                    className={`grid flex-1 gap-3 ${
                        isFiveLayout
                            ? 'grid-cols-2 lg:grid-cols-6'
                            : 'grid-cols-2'
                    }`}
                >
                    {visibleSignals.map((signal, index) => {
                        const spanClass = isFiveLayout
                            ? index < 3
                                ? 'lg:col-span-2'
                                : 'lg:col-span-3'
                            : '';
                        const labelKey = `facilities.trial.signalLabels.${signal.key}`;
                        const labelOverride = t(labelKey, { defaultValue: '' });
                        const overriddenSignal = labelOverride
                            ? { ...signal, label: labelOverride }
                            : signal;
                        return (
                            <SignalCard
                                key={signal.key}
                                {...toSignalCardProps(
                                    overriddenSignal,
                                    TRIAL_SIGNAL_CONFIGS[signal.key]
                                )}
                                className={spanClass}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
